import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import RequirePageView from './RequirePageView'
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Stack,
} from '@mui/material'
import {
  Logout,
  Person,
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  ChevronRight,
  VisibilityOutlined,
  LockOutlined,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { logout, setPermissions, setUser } from '../store/slices/authSlice'
import { useLazyGetMeQuery, useGetMeQuery, useChangeMyPasswordMutation } from '../store/api/authApi'
import { useLazyGetSettingRolesQuery } from '../store/api/settingsApi'
import { deriveNavPermissions } from '../auth/permissions'
import ChangeMyPasswordDialog from './ChangeMyPasswordDialog'
import { API_BASE_URL } from '../config/api'
import Logo from './Logo'
import { navigation } from '../config/navigation'
import type { NavItem } from '../config/navigation'
import type { PermissionLevel } from '../types/api'
import ChatWidget from '../agent/ChatWidget'
import { AgentBridgeProvider } from '../agent/bridgeRegistry'

const drawerWidth = 198
const drawerCollapsedWidth = 56

export default function Layout() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { token, user, permissions } = useAppSelector((state) => state.auth)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [pwDialogOpen, setPwDialogOpen] = useState(false)
  const [changeMyPassword, { isLoading: isChangingMyPassword }] = useChangeMyPasswordMutation()
  const { data: meData } = useGetMeQuery()
  const avatarUrl = meData?.avatar_path ? `${API_BASE_URL}/${meData.avatar_path}` : undefined
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set())
  const [navCollapsed, setNavCollapsed] = useState(false)

  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.classList.remove('theme-light', 'theme-dark')
    document.body.classList.add('theme-light')
  }, [])

  // Re-fetch user + permissions để session đang mở luôn có permission mới nhất
  // (admin có thể đổi role × function bất kỳ lúc nào). 4 trigger:
  //   1. Mount (F5 reload, tab restore)
  //   2. SSE event `perm-changed` từ BE — real-time push khi admin Settings save
  //   3. Polling 5 phút fallback — bảo hiểm SSE drop (proxy idle, BE restart)
  //      hoặc multi-worker BE chưa có Redis pub/sub
  //   4. Event `tlc:perm-refresh` từ `baseApi` khi gặp 403 — bắt revoke ngay
  //      sau lần fail đầu (defense kép với SSE)
  //
  // Debounce 2s để 2+ trigger gần nhau không spam API. 401/network ignored —
  // apiSlice unauth handler tự logout nếu token hết hạn.
  const [triggerGetMe] = useLazyGetMeQuery()
  const [triggerGetRoles] = useLazyGetSettingRolesQuery()
  const lastRefetchRef = useRef(0)

  const refreshPermissions = useCallback(async () => {
    if (!token) return
    const now = Date.now()
    if (now - lastRefetchRef.current < 2000) return // debounce
    lastRefetchRef.current = now
    try {
      const userData = await triggerGetMe(undefined, false).unwrap()
      dispatch(setUser(userData))
      const rolesData = await triggerGetRoles(undefined, false).unwrap()
      const userRole = rolesData.items.find(
        (r) => r.roleCodeUper.toUpperCase() === (userData.role ?? '').toUpperCase(),
      )
      if (userRole) dispatch(setPermissions(deriveNavPermissions(userRole)))
    } catch {
      // 401 / network — giữ stale perms, apiSlice unauth handler xử lý
    }
  }, [token, triggerGetMe, triggerGetRoles, dispatch])

  // (1) Mount-once re-fetch
  useEffect(() => {
    void refreshPermissions()
  }, [refreshPermissions])

  // (2) SSE — BE push event `perm-changed` khi admin Settings save role permission.
  // EventSource auto-reconnect với exponential backoff khi network drop.
  // Token đi qua query param (EventSource không hỗ trợ custom header).
  useEffect(() => {
    if (!token) return
    const url = `${API_BASE_URL}/auth/perm-stream?token=${encodeURIComponent(token)}`
    const es = new EventSource(url)
    es.addEventListener('perm-changed', () => {
      void refreshPermissions()
    })
    es.addEventListener('error', () => {
      // Để EventSource tự reconnect — không dispatch event lỗi cho user
    })
    return () => es.close()
  }, [token, refreshPermissions])

  // (3) Polling 5 phút fallback — bảo hiểm khi SSE silently dropped (corporate
  // proxy đóng dù có heartbeat) hoặc multi-worker BE miss broadcast. Đủ thưa
  // để traffic không đáng kể (~5MB/user/ngày).
  useEffect(() => {
    if (!token) return
    const id = setInterval(() => void refreshPermissions(), 5 * 60_000)
    return () => clearInterval(id)
  }, [token, refreshPermissions])

  // (4) Sự kiện từ baseApi 403 — refetch ngay sau khi BE từ chối, để UI cập nhật
  useEffect(() => {
    const handler = () => void refreshPermissions()
    window.addEventListener('tlc:perm-refresh', handler)
    return () => window.removeEventListener('tlc:perm-refresh', handler)
  }, [refreshPermissions])

  // Auto-expand parents that match current path
  useEffect(() => {
    const expand = new Set<string>()
    const walk = (items: NavItem[]) => {
      for (const item of items) {
        if (item.children) {
          const match = isChildActive(item)
          if (match) expand.add(item.key)
          walk(item.children)
        }
      }
    }
    walk(navigation)
    setOpenKeys((prev) => {
      const merged = new Set(prev)
      expand.forEach((k) => merged.add(k))
      return merged
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  const handleLogout = () => {
    setAnchorEl(null)
    dispatch(logout())
    navigate('/login')
  }

  const handleChangeMyPassword = async (payload: {
    currentPassword: string
    newPassword: string
    confirmPassword: string
  }) => {
    await changeMyPassword(payload).unwrap()
  }

  /** Find ancestor keys of a given key in the nav tree */
  const getAncestorKeys = (targetKey: string, items: NavItem[] = navigation, parents: string[] = []): string[] | null => {
    for (const item of items) {
      if (item.key === targetKey) return parents
      if (item.children) {
        const found = getAncestorKeys(targetKey, item.children, [...parents, item.key])
        if (found) return found
      }
    }
    return null
  }

  /**
   * Tìm leaf đầu tiên có level KHỚP `target` ('Edit' | 'View') trong nhánh, kèm chain key.
   * Trả null nếu không có leaf nào match. inheritedPermKey để leaf không có permKey riêng
   * vẫn lookup đúng theo ancestor.
   */
  const findLeafOfLevel = (
    item: NavItem,
    target: 'Edit' | 'View',
    inheritedPermKey?: string,
  ): { keys: string[]; path: string } | null => {
    if (!item.children || item.children.length === 0) {
      const lvl = getItemLevel(item, inheritedPermKey)
      return lvl === target ? { keys: [], path: item.path } : null
    }
    const childInheritedPermKey = item.permKey ?? inheritedPermKey
    for (const child of item.children) {
      const sub = findLeafOfLevel(child, target, childInheritedPermKey)
      if (sub) return { keys: [item.key, ...sub.keys], path: sub.path }
    }
    return null
  }

  /**
   * Walk to find the BEST-LEVEL accessible leaf (Edit ưu tiên trước View) + chain key.
   * Trả null nếu cả nhánh đều NoGrant. Defense: parent NoGrant đã bị disabled.
   */
  const findFirstLeafChain = (
    item: NavItem,
    inheritedPermKey?: string,
  ): { keys: string[]; path: string } | null => {
    if (!item.children || item.children.length === 0) {
      const lvl = getItemLevel(item, inheritedPermKey)
      return lvl === 'NoGrant' ? null : { keys: [], path: item.path }
    }
    const edit = findLeafOfLevel(item, 'Edit', inheritedPermKey)
    if (edit) return edit
    const view = findLeafOfLevel(item, 'View', inheritedPermKey)
    if (view) return view
    return null
  }

  /**
   * Click handler for a parent (has-children) nav item.
   *   - If currently closed → expand it + first ACCESSIBLE leaf chain, navigate to that leaf.
   *   - If currently open  → close it (no navigation).
   */
  const handleParentClick = (item: NavItem, inheritedPermKey?: string) => {
    if (openKeys.has(item.key)) {
      setOpenKeys((prev) => {
        const next = new Set(prev)
        next.delete(item.key)
        return next
      })
      return
    }
    const ancestors = getAncestorKeys(item.key) ?? []
    const target = findFirstLeafChain(item, inheritedPermKey)
    if (!target) return // parent NoGrant — disabled handler đã chặn, đây là defense
    const { keys: chain, path } = target
    setOpenKeys(new Set([...ancestors, ...chain]))
    if (path && !isPathActive(path)) {
      navigate(path)
      if (isMobile) setMobileOpen(false)
    }
  }

  /** Check if a path (possibly with query) matches the current location */
  const isPathActive = (path: string): boolean => {
    if (path.includes('?')) {
      const [base, qs] = path.split('?')
      return location.pathname === base && location.search.includes(qs)
    }
    // Khớp cả route con: leaf `/baoloc` vẫn "selected" khi FactoryPage điều hướng
    // sang `/baoloc/...`. Ranh giới `/` để `/baoloc` không khớp nhầm `/baolocxyz`.
    // An toàn vì không leaf nav nào là prefix của leaf khác.
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  /** Check if any child of a parent is active */
  const isChildActive = (item: NavItem): boolean => {
    if (!item.children) return isPathActive(item.path)
    return item.children.some((child) => isChildActive(child))
  }

  const hasNavPerms = Object.keys(permissions).length > 0

  // Resolve permission cho 1 permKey cụ thể (kèm fallback parent dot-segment).
  // Fail-open khi permissions chưa load.
  const resolvePermKey = (permKey: string): PermissionLevel => {
    if (!hasNavPerms) return 'Edit'
    const direct = permissions[permKey]
    if (direct !== undefined) return direct
    if (permKey.includes('.')) {
      const parent = permissions[permKey.split('.')[0]]
      if (parent !== undefined) return parent
    }
    return 'NoGrant'
  }

  // Effective level cho 1 nav item (đệ quy với permKey kế thừa từ ancestor).
  // - Nếu item có permKey riêng → dùng nó (override ancestor).
  // - Nếu item là parent (có children) và có permKey → level = chính nó (không max child),
  //   vì BE thường gắn quyền ở cấp nhóm và children inherit. Tránh case Settings/roles không
  //   có permKey lại fail-open 'Edit' trong khi 'setting' = NoGrant.
  // - Item không permKey + không inherited → 'Edit' (giữ fail-open cho item không phân quyền).
  const RANK: Record<PermissionLevel, number> = { NoGrant: 0, View: 1, Edit: 2 }
  const FROM_RANK: Record<number, PermissionLevel> = { 0: 'NoGrant', 1: 'View', 2: 'Edit' }
  const getItemLevel = (item: NavItem, inheritedPermKey?: string): PermissionLevel => {
    const effectivePermKey = item.permKey ?? inheritedPermKey
    if (item.children && item.children.length > 0) {
      // Parent có permKey rõ ràng → level từ permKey đó, children inherit
      if (item.permKey) return resolvePermKey(item.permKey)
      // Parent không có permKey → max children (mỗi child tự lookup hoặc kế thừa inheritedPermKey)
      let max = 0
      for (const c of item.children) {
        const r = RANK[getItemLevel(c, effectivePermKey)]
        if (r > max) max = r
      }
      return FROM_RANK[max]
    }
    // Leaf
    if (effectivePermKey) return resolvePermKey(effectivePermKey)
    return 'Edit'
  }

  // 3 cấp permission: Edit = default; View = chữ teal + icon mắt cuối row;
  // NoGrant = chữ đỏ mờ + icon khoá + opacity 0.5 + cursor not-allowed.
  // Màu chọn theo light theme (nền cream, chữ teal đậm).
  const VIEW_COLOR = '#2f7a78'      // teal đậm vừa
  const NOGRANT_COLOR = '#a13a3a'   // đỏ-nâu trầm, không chói

  const levelItemSx = (lvl: PermissionLevel) => {
    if (lvl === 'NoGrant') {
      return {
        opacity: 0.55,
        cursor: 'not-allowed',
        color: NOGRANT_COLOR,
        backgroundColor: 'rgba(161, 58, 58, 0.05)',
        pointerEvents: 'auto' as const,
        '&:hover': { backgroundColor: 'rgba(161, 58, 58, 0.05)', transform: 'none' },
      }
    }
    if (lvl === 'View') {
      return {
        color: VIEW_COLOR,
      }
    }
    return {}
  }

  // Status icon (Visibility / Lock) thay cho module icon ở slot LEADING khi View/NoGrant.
  // Đặt trước tab — tránh wrap label dài như "Virtual Warehouse".
  // Trả null cho Edit để cho phép caller dùng module icon mặc định.
  const getLevelLeadingIcon = (lvl: PermissionLevel) => {
    if (lvl === 'View') return VisibilityOutlined
    if (lvl === 'NoGrant') return LockOutlined
    return null
  }

  const effectiveDrawerWidth = isMobile ? drawerWidth : navCollapsed ? drawerCollapsedWidth : drawerWidth

  const sidebarSelectedSx = {
    position: 'relative',
    color: 'var(--app-sidebar-text)',
    transition: 'background 160ms ease, color 160ms ease, transform 160ms ease',
    '&.Mui-selected': {
      background: 'var(--app-sidebar-active-bg)',
      color: 'var(--app-sidebar-active-text, var(--app-sidebar-text))',
      boxShadow: 'inset 0 0 0 1px rgba(218, 131, 60, 0.18)',
      '&:hover': { background: 'var(--app-sidebar-active-bg)' },
      '& .MuiListItemIcon-root': {
        color: 'var(--app-sidebar-active-icon)',
      },
      '& .MuiListItemText-primary': {
        fontWeight: 700,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 7,
        bottom: 7,
        width: 3,
        borderRadius: 999,
        backgroundColor: 'var(--app-sidebar-active-border)',
      },
    },
    '&:hover': {
      backgroundColor: 'var(--app-sidebar-hover-bg, rgba(20, 90, 89, 0.075))',
      transform: 'translateX(1px)',
    },
  } as const

  const renderNavItems = (items: NavItem[], depth = 0, inheritedPermKey?: string) =>
    items.map((item) => {
      const Icon = item.icon
      const hasChildren = item.children && item.children.length > 0
      const isOpen = openKeys.has(item.key)
      const active = !hasChildren && isPathActive(item.path)
      const parentActive = hasChildren && isChildActive(item)
      const lvl = getItemLevel(item, inheritedPermKey)
      const childInheritedPermKey = item.permKey ?? inheritedPermKey
      const isNoGrant = lvl === 'NoGrant'
      const isView = lvl === 'View'
      const lvlSx = levelItemSx(lvl)
      // View/NoGrant → thay module icon bằng Visibility/Lock; Edit giữ module icon.
      const LevelIcon = getLevelLeadingIcon(lvl)
      const LeadingIcon = LevelIcon ?? Icon
      const showLeadingIcon = Boolean(LeadingIcon) && (depth <= 1 || Boolean(LevelIcon))

      if (hasChildren) {
        return (
          <Box key={item.key}>
            <ListItem disablePadding sx={{ px: 1, mb: 0.25 }}>
              <ListItemButton
                disabled={isNoGrant}
                onClick={() => {
                  if (isNoGrant) return
                  handleParentClick(item, inheritedPermKey)
                }}
                sx={{
                  pl: 1.5 + depth * 1.5,
                  color: 'var(--app-sidebar-text)',
                  ...(parentActive && !isOpen
                    ? {
                        background: 'var(--app-sidebar-active-bg)',
                        boxShadow: 'inset 0 0 0 1px rgba(218, 131, 60, 0.14)',
                      }
                    : {}),
                  '&:hover': {
                    backgroundColor: 'var(--app-sidebar-hover-bg, rgba(20, 90, 89, 0.075))',
                  },
                  ...lvlSx,
                  '&.Mui-disabled': { opacity: 0.4, pointerEvents: 'auto', cursor: 'not-allowed' },
                }}
              >
                {showLeadingIcon && LeadingIcon && (
                  <ListItemIcon
                    sx={{
                      color: isNoGrant
                        ? NOGRANT_COLOR
                        : isView
                        ? VIEW_COLOR
                        : parentActive
                        ? 'var(--app-sidebar-active-icon)'
                        : 'var(--app-sidebar-icon)',
                      minWidth: 30,
                    }}
                  >
                    <LeadingIcon
                      sx={{
                        fontSize: depth === 0 ? 18 : 16,
                        stroke: 'currentColor',
                        strokeWidth: 0.45,
                        filter: parentActive && !isNoGrant ? 'drop-shadow(0 0 5px rgba(244,223,156,0.35))' : 'none',
                      }}
                    />
                  </ListItemIcon>
                )}
                {!navCollapsed ? (
                  <>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: depth === 0 ? '0.72rem' : '0.68rem',
                        fontWeight: parentActive ? 600 : 400,
                        color: isNoGrant ? NOGRANT_COLOR : isView ? VIEW_COLOR : undefined,
                        lineHeight: 1.2,
                        noWrap: true,
                      }}
                    />
                    {isOpen ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                  </>
                ) : null}
              </ListItemButton>
            </ListItem>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <List disablePadding>{renderNavItems(item.children!, depth + 1, childInheritedPermKey)}</List>
            </Collapse>
          </Box>
        )
      }

      // Purchase + SA Contract: highlight bằng brand green (success.main) trong sidebar.
      // Highlight chỉ áp dụng khi item KHÔNG bị NoGrant (tránh vừa xám vừa xanh).
      const isContractItem = (item.key === 'pucontract' || item.key === 'local-s') && !isNoGrant && !isView

      return (
        <ListItem key={item.key} disablePadding sx={{ px: 1, mb: 0.25 }}>
          <ListItemButton
            selected={active && !isNoGrant}
            disabled={isNoGrant}
            sx={{
              ...sidebarSelectedSx,
              pl: 1.5 + depth * 1.5,
              ...lvlSx,
              '&.Mui-disabled': { opacity: 0.4, pointerEvents: 'auto', cursor: 'not-allowed' },
            }}
            onClick={() => {
              if (isNoGrant) return
              navigate(item.path)
              if (isMobile) setMobileOpen(false)
            }}
          >
            {showLeadingIcon && LeadingIcon && (
              <ListItemIcon
                sx={{
                  color: isNoGrant
                    ? NOGRANT_COLOR
                    : isView
                    ? VIEW_COLOR
                    : isContractItem
                    ? 'success.main'
                    : active
                    ? 'var(--app-sidebar-active-icon)'
                    : 'var(--app-sidebar-icon)',
                  minWidth: 24,
                }}
              >
                <LeadingIcon
                  sx={{
                    fontSize: depth === 0 ? 18 : 16,
                    stroke: 'currentColor',
                    strokeWidth: 0.45,
                    filter: active && !isNoGrant ? 'drop-shadow(0 0 5px rgba(244,223,156,0.35))' : 'none',
                  }}
                />
              </ListItemIcon>
            )}
            {!navCollapsed ? (
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: depth === 0 ? '0.72rem' : '0.68rem',
                  fontWeight: isContractItem ? 700 : active && !isNoGrant ? 700 : 500,
                  color: isNoGrant
                    ? NOGRANT_COLOR
                    : isView
                    ? VIEW_COLOR
                    : isContractItem
                    ? 'success.main'
                    : undefined,
                  lineHeight: 1.2,
                  noWrap: true,
                }}
              />
            ) : null}
          </ListItemButton>
        </ListItem>
      )
    })

  // Derive page title from active nav item
  const findTitle = (items: NavItem[]): string | undefined => {
    for (const item of items) {
      if (!item.children && isPathActive(item.path)) return item.label
      if (item.children) {
        const sub = findTitle(item.children)
        if (sub) return sub
      }
    }
    return undefined
  }
  const title = findTitle(navigation) ?? 'ERP'
  const sectionTitle = (() => {
    const top = navigation.find((item) => location.pathname.startsWith(item.path) && item.path !== '/')
    return top?.label ?? 'Workspace'
  })()
  return (
    <AgentBridgeProvider>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          // height (KHÔNG minHeight) để khoá app-shell đúng 1 viewport: `<main>`
          // (overflow:auto) trở thành vùng scroll nội bộ → page không dài theo
          // content (AppBar + Drawer + title/nav trong page giữ cố định khi cuộn).
          height: '100vh',
          overflow: 'hidden',
          background:
            'radial-gradient(circle at 92% 0%, rgba(244, 223, 156, 0.24), transparent 28%), var(--app-bg)',
        }}
      >
      <AppBar
        position="fixed"
        color="transparent"
        sx={{
          background: 'var(--app-topbar-bg)',
          color: 'var(--app-topbar-text, var(--app-sidebar-text))',
          width: '100%',
          ml: 0,
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: { xs: 48, sm: 56 } }}>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          {!isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setNavCollapsed((v) => !v)}
              sx={{ mr: 1 }}
              aria-label={navCollapsed ? 'Show navigation' : 'Hide navigation'}
            >
              {navCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          )}
          <Logo variant="topbar" showText />
          <Box sx={{ ml: { xs: 1.5, sm: 2.5 }, flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  // xs: ẩn feature title cho khỏi sát logo "ERP CTRM" trên màn nhỏ.
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: 800,
                  letterSpacing: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: 1.1,
                  fontSize: { xs: '0.92rem', sm: '1rem' },
                }}
              >
                {title}
              </Typography>
              <Chip
                size="small"
                label={sectionTitle}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  height: 22,
                  borderRadius: 999,
                  bgcolor: 'rgba(244, 223, 156, 0.18)',
                  color: '#f4df9c',
                  border: '1px solid rgba(244, 223, 156, 0.36)',
                  fontWeight: 800,
                  fontSize: '0.66rem',
                  letterSpacing: 0.4,
                }}
              />
            </Stack>
          </Box>
          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar src={avatarUrl} sx={{ width: 32, height: 32 }}>
              {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, ml: 1 }}>
            {user?.full_name || user?.username}
          </Typography>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile') }}>
              <ListItemIcon><Person fontSize="small" /></ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); setPwDialogOpen(true) }}>
              <ListItemIcon><LockOutlined fontSize="small" /></ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={isMobile ? { keepMounted: true } : undefined}
          sx={{
            width: effectiveDrawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: effectiveDrawerWidth,
              boxSizing: 'border-box',
              backgroundColor: 'var(--app-sidebar-bg)',
              backgroundImage:
                'radial-gradient(circle at 12% 4%, rgba(244,223,156,0.28), transparent 30%), linear-gradient(180deg, rgba(20,90,89,0.075), rgba(255,249,244,0.00) 40%)',
              color: 'var(--app-sidebar-text)',
              borderRight: '1px solid rgba(11, 54, 51, 0.13)',
              top: { xs: 48, sm: 56 },
              height: { xs: 'calc(100% - 48px)', sm: 'calc(100% - 56px)' },
              overflowX: 'hidden',
              transition: 'width 160ms ease',
            },
          }}
        >
          <Divider />
          <List sx={{ pt: 1, overflowY: 'auto', flex: 1 }}>
            {renderNavItems(navigation)}
          </List>
          <Divider />
          <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={avatarUrl} sx={{ width: 32, height: 32 }}>
              {(user?.full_name || user?.username || 'U').charAt(0).toUpperCase()}
            </Avatar>
            {!navCollapsed ? (
              <Typography variant="body2" noWrap sx={{ flex: 1, fontSize: '0.75rem' }}>
                {user?.full_name || user?.username}
              </Typography>
            ) : null}
            <IconButton size="small" onClick={handleLogout}><Logout fontSize="small" /></IconButton>
          </Box>
        </Drawer>
          <Box
            component="main"
            sx={{
              flex: 1,
              p: { xs: 0.75, sm: 1, md: 1.5 },
              mt: { xs: 6, sm: 7 },
              overflow: 'auto',
              background:
                'linear-gradient(180deg, rgba(255,249,244,0.78), rgba(251,246,239,0.96) 220px), radial-gradient(circle at 100% 0%, rgba(218,131,60,0.10), transparent 24%)',
            }}
          >
            <RequirePageView>
              <Outlet />
            </RequirePageView>
          </Box>
        </Box>
        <ChatWidget />
        <ChangeMyPasswordDialog
          open={pwDialogOpen}
          email={user?.email || user?.username || ''}
          onClose={() => setPwDialogOpen(false)}
          onSubmit={handleChangeMyPassword}
          isSubmitting={isChangingMyPassword}
        />
      </Box>
    </AgentBridgeProvider>
  )
}
